import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/base/SEOMeta';
import ScrollToTop from '@/components/base/ScrollToTop';
import VideoCard from '@/components/feature/VideoCard';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, videos, articles } from '@/mocks/data';

export default function Home() {
  const hotVideos = videos.filter((v) => v.isHot).slice(0, 8);
  const newVideos = videos.filter((v) => v.isNew).slice(0, 8);
  const featuredArticles = articles.slice(0, 3);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '黄瓜tv',
    url: 'https://bjhongfa.cn',
    description: '黄瓜tv - 高清视频在线观看平台，提供海量高清影视资源、流畅播放体验与丰富内容分类。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://bjhongfa.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黄瓜tv',
    url: 'https://bjhongfa.cn',
    description: '黄瓜tv - 高清视频在线观看平台',
    sameAs: ['https://weibo.com/huanggua', 'https://weixin.qq.com/huanggua'],
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="首页"
        description="黄瓜tv - 高清视频在线观看平台，提供海量高清影视资源、流畅播放体验与丰富内容分类。无论是影视、综艺还是短视频，黄瓜tv都能满足你的观看需求。"
        keywords="黄瓜tv,黄瓜tv在线观看,黄瓜tv高清视频,黄瓜tv最新版,黄瓜tv下载,黄瓜tv官网入口"
        canonicalUrl="https://bjhongfa.cn/"
        schema={{ ...websiteSchema, ...organizationSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20home%20theater%20room%20with%20large%20screen%20displaying%20cinematic%20content%2C%20warm%20ambient%20lighting%20with%20golden%20and%20coral%20accents%2C%20cozy%20modern%20furniture%2C%20abstract%20geometric%20shapes%20floating%2C%20artistic%20cinematic%20atmosphere%2C%20rich%20warm%20colors&width=1800&height=900&seq=hero-bg-home-01&orientation=landscape"
              alt="黄瓜tv高清视频在线观看"
              className="absolute inset-0 w-full h-full object-cover object-top"
              title="黄瓜tv - 高清视频在线观看平台"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
            <div className="relative z-10 w-full px-4 md:px-6 text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/90 text-background-50 text-sm font-medium mb-6">
                高清视频在线观看平台
              </span>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 font-heading leading-tight">
                随时随地，畅享<span className="text-primary-400">高清视界</span>
              </h1>
              <p className="text-base md:text-lg text-background-50/80 max-w-2xl mx-auto mb-8 leading-relaxed">
                海量影视综艺资源，流畅高清播放体验，黄瓜tv为你打造极致观影享受
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link
                  to="/category/dianshi"
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  立即观看
                </Link>
                <Link
                  to="/about"
                  className="px-6 py-3 bg-background-50/20 hover:bg-background-50/30 text-background-50 font-medium rounded-full backdrop-blur-sm border border-background-50/30 transition-colors whitespace-nowrap cursor-pointer"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>

          {/* Category Grid */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                丰富内容分类
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                涵盖影视综艺动漫等多种类型，总有你喜欢的
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-300">
                    <i className={`${cat.icon} text-2xl`}></i>
                  </div>
                  <span className="text-sm md:text-base font-semibold text-foreground-900 mb-1">{cat.name}</span>
                  <span className="text-xs text-foreground-500">{cat.description}</span>
                </Link>
              ))}
            </div>
          </section>

          {/* Hot Videos */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-600">大家都在看的热门内容</p>
              </div>
              <Link
                to="/category/dianshi"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {hotVideos.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          </section>

          {/* Latest Videos */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  最新上线
                </h2>
                <p className="text-sm text-foreground-600">刚出炉的新鲜内容</p>
              </div>
              <Link
                to="/category/dianying"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {newVideos.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          </section>

          {/* Stats / Features */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {[
                { value: '2.8亿+', label: '累计用户', icon: 'ri-user-line' },
                { value: '100万+', label: '内容数量', icon: 'ri-film-line' },
                { value: '5亿次+', label: '日均播放', icon: 'ri-play-circle-line' },
                { value: '4K HDR', label: '高清画质', icon: 'ri-4k-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mx-auto mb-3">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Article Highlights */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  影视资讯
                </h2>
                <p className="text-sm text-foreground-600">最新影视动态和深度分析</p>
              </div>
              <Link
                to="/category/zixun"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                更多资讯 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}